import React from "react";
import { motion } from "framer-motion";

const SkeletonBase = ({ className = "", animate = true, children }) => {
  const shimmer = {
    hidden: { x: "-100%" },
    visible: {
      x: "100%",
      transition: {
        duration: 1.5,
        ease: "easeInOut",
        repeat: Infinity,
        repeatDelay: 0.5,
      },
    },
  };

  return (
    <div
      className={`relative overflow-hidden bg-gray-300 dark:bg-gray-700 rounded ${className}`}
    >
      {children}
      {animate && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 dark:via-gray-600/50 to-transparent"
          variants={shimmer}
          initial="hidden"
          animate="visible"
        />
      )}
    </div>
  );
};

// Blog Card Skeleton
export const BlogCardSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-lg p-6 border border-gray-200 dark:border-gray-700">
    <SkeletonBase className="h-6 w-3/4 mb-4" />
    <div className="flex items-center mb-4">
      <SkeletonBase className="h-4 w-4 rounded mr-2" animate={false} />
      <SkeletonBase className="h-4 w-20" />
    </div>
    <SkeletonBase className="h-4 w-full mb-2" />
    <SkeletonBase className="h-4 w-full mb-2" />
    <SkeletonBase className="h-4 w-2/3 mb-4" />
    <SkeletonBase className="h-10 w-full" />
  </div>
);

// Project Card Skeleton
export const ProjectCardSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700">
    <SkeletonBase className="h-64 md:h-72 w-full" />
    <div className="p-4">
      <SkeletonBase className="h-6 w-3/4 mb-3" />
      <SkeletonBase className="h-4 w-full mb-2" />
      <SkeletonBase className="h-4 w-full mb-2" />
      <SkeletonBase className="h-4 w-1/2 mb-4" />
      <SkeletonBase className="h-10 w-full" />
    </div>
  </div>
);

// Page Content Skeleton
export const PageContentSkeleton = () => (
  <div className="container mx-auto p-4 pb-40 max-w-6xl">
    <SkeletonBase className="h-12 w-2/3 mx-auto mb-8" />
    <div className="space-y-6">
      <SkeletonBase className="h-4 w-full" />
      <SkeletonBase className="h-4 w-full" />
      <SkeletonBase className="h-4 w-3/4" />
      <SkeletonBase className="h-4 w-full" />
      <SkeletonBase className="h-4 w-5/6" />
    </div>
  </div>
);

// Grid Skeleton for multiple items
export const GridSkeleton = ({
  count = 6,
  component: Component = BlogCardSkeleton,
  className = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6",
}) => (
  <div className={className}>
    {Array.from({ length: count }, (_, index) => (
      <Component key={index} />
    ))}
  </div>
);

// Generic Loading Screen
export const LoadingScreen = ({ message = "Loading..." }) => (
  <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
    <motion.div
      className="flex space-x-2"
      initial="hidden"
      animate="visible"
      variants={{
        visible: {
          transition: {
            staggerChildren: 0.2,
          },
        },
      }}
    >
      {[0, 1, 2].map((index) => (
        <motion.div
          key={index}
          className="w-3 h-3 bg-brand-gold rounded-full"
          variants={{
            hidden: { opacity: 0.3, scale: 0.8 },
            visible: {
              opacity: 1,
              scale: 1,
              transition: {
                duration: 0.6,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut",
              },
            },
          }}
        />
      ))}
    </motion.div>
    <p className="text-gray-700 dark:text-brand-cream text-lg">{message}</p>
  </div>
);

// Text Skeleton
export const TextSkeleton = ({ lines = 3, className = "" }) => (
  <div className={`space-y-3 ${className}`}>
    {Array.from({ length: lines }, (_, index) => (
      <SkeletonBase
        key={index}
        className={`h-4 ${
          index === lines - 1 ? "w-2/3" : index % 2 === 0 ? "w-full" : "w-5/6"
        }`}
      />
    ))}
  </div>
);

// Avatar Skeleton
export const AvatarSkeleton = ({ size = "md" }) => {
  const sizes = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16",
    xl: "w-20 h-20",
  };

  return <SkeletonBase className={`${sizes[size]} rounded-full`} />;
};

// Button Skeleton
export const ButtonSkeleton = ({ className = "h-10 w-32" }) => (
  <SkeletonBase className={`rounded-lg ${className}`} />
);

export default SkeletonBase;
